import { Group, Tween } from "@tweenjs/tween.js"
import React, { ReactNode, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import ResizeObserver from "resize-observer-polyfill"
import { cls } from "../cls"
import { getEasing, getResponsiveSettings, getStartingIndex } from "./helpers"
import styles from "./ImageSlider.module.css"
import { ButtonClick, Responsive, SlideProps, SlideshowRef } from "./types"

/**
 * This component and related files have been copied from https://github.com/femioladeji/react-slideshow and modified
 *
 * See LICENSE file
 */

export const ImageSlider = React.forwardRef<SlideshowRef, SlideProps>((props, ref) => {
	const [index, setIndex] = useState(getStartingIndex(props.children, props.defaultIndex))
	const [wrapperSize, setWrapperSize] = useState<number>(0)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const innerWrapperRef = useRef<any>(null)
	const tweenGroup = useRef(new Group())

	const responsiveSettings = useMemo(
		() => getResponsiveSettings(wrapperSize, props.responsive),
		[wrapperSize, props.responsive],
	)

	const slidesToScroll = useMemo(() => {
		if (responsiveSettings) {
			return responsiveSettings.settings.slidesToScroll
		}
		return props.slidesToScroll || 1
	}, [responsiveSettings, props.slidesToScroll])
	const slidesToShow = useMemo(() => {
		if (responsiveSettings) {
			return responsiveSettings.settings.slidesToShow
		}
		return props.slidesToShow || 1
	}, [responsiveSettings, props.slidesToShow])

	const childrenCount = useMemo(() => React.Children.count(props.children), [props.children])
	const eachChildSize = useMemo(() => wrapperSize / slidesToShow, [wrapperSize, slidesToShow])
	const timeout = useRef<NodeJS.Timeout>()
	const resizeObserver = useRef<ResizeObserver | null>()

	let startingSwipeXPosition: number
	let startingSwipeYPosition: number
	let dragging: boolean = false
	let distanceSwipedX: number = 0
	let swipedTooFarY = false

	const swipeThreshold = useRef<number>(props.swipeThreshold !== undefined ? props.swipeThreshold : 0)
	const swipeHasBegun = useRef<boolean>(false)

	const controller = useRef(new AbortController())

	useImperativeHandle(ref, () => ({
		goNext: () => {
			moveNext()
		},
		goBack: () => {
			moveBack()
		},
		goTo: (index: number, options?: { skipTransition?: boolean }) => {
			if (options?.skipTransition) {
				setIndex(index)
			} else {
				moveTo(index)
			}
		},
	}))

	const applyStyle = useCallback(() => {
		if (innerWrapperRef.current) {
			const fullSize = wrapperSize * innerWrapperRef.current.children.length
			innerWrapperRef.current.style.width = `${fullSize}px`

			for (let index = 0; index < innerWrapperRef.current.children.length; index++) {
				const eachDiv = innerWrapperRef.current.children[index]
				if (eachDiv) {
					eachDiv.style.width = `${eachChildSize}px`
					eachDiv.style.display = `block`
				}
			}
		}
	}, [wrapperSize, eachChildSize])

	const initResizeObserver = useCallback(() => {
		if (wrapperRef.current) {
			resizeObserver.current = new ResizeObserver((entries) => {
				if (!entries) return
				setSize()
			})
			resizeObserver.current.observe(wrapperRef.current)
		}
	}, [wrapperRef])

	const play = useCallback(() => {
		const { autoplay, infinite, duration } = props
		if (autoplay && (infinite || index < childrenCount - 1)) {
			timeout.current = setTimeout(moveNext, duration)
		}
	}, [props, childrenCount, index])

	useEffect(() => {
		applyStyle()
	}, [wrapperSize, applyStyle])

	useEffect(() => {
		initResizeObserver()
		const twGroup = tweenGroup.current
		return () => {
			twGroup.removeAll()
			clearTimeout(timeout.current)
			removeResizeObserver()
		}
	}, [wrapperRef, initResizeObserver, tweenGroup])

	useEffect(() => {
		clearTimeout(timeout.current)
		play()
	}, [index, wrapperSize, props.autoplay, play])

	useEffect(() => {
		const abortController = controller.current
		document.addEventListener(
			"wheel",
			function (event) {
				if (swipeHasBegun.current) {
					try {
						event.preventDefault()
					} catch (e) {}
					return
				}
			},
			{ passive: false, signal: controller.current.signal },
		)

		document.addEventListener(
			"touchmove",
			function (event) {
				if (swipeHasBegun.current) {
					try {
						event.preventDefault()
						event.stopImmediatePropagation()
					} catch (e) {}
					return
				}
			},
			{ passive: false, signal: controller.current.signal },
		)

		return () => {
			abortController.abort()
		}
	}, [])

	const removeResizeObserver = () => {
		if (resizeObserver.current && wrapperRef.current) {
			resizeObserver.current.unobserve(wrapperRef.current)
		}
	}

	const pauseSlides = () => {
		if (props.pauseOnHover) {
			clearTimeout(timeout.current)
		}
	}

	const swipe = (event: React.MouseEvent | React.TouchEvent) => {
		if (props.canSwipe && dragging && !swipedTooFarY) {
			let positionX: number
			let positionY: number
			if (window.TouchEvent && event.nativeEvent instanceof TouchEvent) {
				positionX = event.nativeEvent.touches?.[0]?.pageX || 0
				positionY = event.nativeEvent.touches?.[0]?.pageY || 0
			} else {
				positionX = (event.nativeEvent as MouseEvent).pageX
				positionY = (event.nativeEvent as MouseEvent).pageY
			}
			if (positionX && startingSwipeXPosition) {
				let translateValueX = eachChildSize * (index + getOffset())
				const distanceX = positionX - startingSwipeXPosition
				const distanceY = positionY - startingSwipeYPosition
				if (!props.infinite && index === childrenCount - slidesToScroll && distanceX < 0) {
					// if it is the last and infinite is false and you're swiping left
					// then nothing happens
					return
				}
				if (!props.infinite && index === 0 && distanceX > 0) {
					// if it is the first and infinite is false and you're swiping right
					// then nothing happens
					return
				}

				/*
					If we haven't started the X-axis scrolling we disable it, if you've started scrolling
					a little in the Y-axis.

					Combine this with the swipeHasBegun variable, and we can stop x-axis scroll during y-axis scroll,
					or vice versa.
				 */
				if ((distanceY > 10 || distanceY < -10) && !swipeHasBegun.current) {
					swipedTooFarY = true
					return
				}
				if (distanceX < -swipeThreshold.current || distanceX > swipeThreshold.current || swipeHasBegun.current) {
					swipeHasBegun.current = true
					distanceSwipedX = distanceX
					translateValueX -= distanceSwipedX
					innerWrapperRef.current.style.transform = `translateX(-${translateValueX}px)`
				}
			}
		}
	}

	const moveNext = () => {
		if (!props.infinite && index === childrenCount - slidesToScroll) {
			return
		}
		const nextIndex = calculateIndex(index + slidesToScroll)
		transitionSlide(nextIndex)
	}

	const moveBack = () => {
		if (!props.infinite && index === 0) {
			return
		}
		let previousIndex = index - slidesToScroll
		if (previousIndex % slidesToScroll) {
			previousIndex = Math.ceil(previousIndex / slidesToScroll) * slidesToScroll
		}
		transitionSlide(previousIndex)
	}

	const moveTo = (index: number) => {
		transitionSlide(calculateIndex(index))
	}

	const calculateIndex = (nextIndex: number): number => {
		if (nextIndex < childrenCount && nextIndex + slidesToScroll > childrenCount) {
			if ((childrenCount - slidesToScroll) % slidesToScroll) {
				return childrenCount - slidesToScroll
			}
			return nextIndex
		}
		return nextIndex
	}

	const startSlides = () => {
		if (dragging) {
			endSwipe()
		} else if (props.pauseOnHover && props.autoplay) {
			timeout.current = setTimeout(moveNext, props.duration)
		}
	}

	const moveSlides: ButtonClick = ({ currentTarget: { dataset } }) => {
		if (dataset.type === "next") {
			moveNext()
		} else {
			moveBack()
		}
	}

	const renderPrecedingSlides = () => {
		return React.Children.toArray(props.children)
			.slice(-slidesToShow)
			.map((each, index) => (
				<div
					data-index={index - slidesToShow}
					aria-roledescription="slide"
					aria-hidden="true"
					key={index - slidesToShow}>
					{each}
				</div>
			))
	}

	const renderTrailingSlides = () => {
		if (!props.infinite && slidesToShow === slidesToScroll) {
			return
		}
		return React.Children.toArray(props.children)
			.slice(0, slidesToShow)
			.map((each, index) => (
				<div
					data-index={childrenCount + index}
					aria-roledescription="slide"
					aria-hidden="true"
					key={childrenCount + index}>
					{each}
				</div>
			))
	}

	const setSize = () => {
		if (wrapperRef.current) {
			setWrapperSize(wrapperRef.current.clientWidth)
		}
	}

	const startSwipe = (event: React.MouseEvent | React.TouchEvent) => {
		if (props.canSwipe) {
			if (window.TouchEvent && event.nativeEvent instanceof TouchEvent) {
				startingSwipeXPosition = event.nativeEvent.touches?.[0]?.pageX || 0
				startingSwipeYPosition = event.nativeEvent.touches?.[0]?.pageY || 0
			} else if (event.nativeEvent instanceof MouseEvent) {
				startingSwipeXPosition = event.nativeEvent.pageX
				startingSwipeYPosition = event.nativeEvent.pageY
			}
			clearTimeout(timeout.current)
			dragging = true
		}
	}

	const endSwipe = () => {
		swipedTooFarY = false
		if (props.canSwipe) {
			dragging = false
			if (Math.abs(distanceSwipedX) / wrapperSize > 0.2) {
				if (distanceSwipedX < 0) {
					moveNext()
				} else {
					moveBack()
				}
			} else {
				if (Math.abs(distanceSwipedX) > 0) {
					transitionSlide(index, 300)
				}
			}
		}
	}

	const animate = () => {
		requestAnimationFrame(animate)
		tweenGroup.current.update()
	}

	const transitionSlide = (toIndex: number, animationDuration?: number) => {
		const transitionDuration = animationDuration || props.transitionDuration
		const currentIndex = index
		const existingTweens = tweenGroup.current.getAll()
		if (!wrapperRef.current) {
			return
		}

		const childSize = wrapperRef.current.clientWidth / slidesToShow
		if (!existingTweens.length) {
			clearTimeout(timeout.current)
			const value = {
				margin: -childSize * (currentIndex + getOffset()) + distanceSwipedX,
			}
			const tween = new Tween(value, tweenGroup.current)
				.to({ margin: -childSize * (toIndex + getOffset()) }, transitionDuration)
				.onUpdate((value) => {
					if (innerWrapperRef.current) {
						innerWrapperRef.current.style.transform = `translateX(${value.margin}px)`
					}
				})
			tween.easing(getEasing(props.easing))

			animate()

			let newIndex = toIndex
			if (newIndex < 0) {
				newIndex = childrenCount - slidesToScroll
			} else if (newIndex >= childrenCount) {
				newIndex = 0
			}

			tween.onStart(() => {
				if (typeof props.onStartChange === "function") {
					props.onStartChange(index, newIndex)
				}
			})

			tween.onComplete(() => {
				distanceSwipedX = 0
				swipeHasBegun.current = false
				if (typeof props.onChange === "function") {
					props.onChange(index, newIndex)
				}
				setIndex(newIndex)
			})

			tween.start()
		}
	}

	const isSlideActive = (key: number) => {
		return key < index + slidesToShow && key >= index
	}

	const getOffset = (): number => {
		if (!props.infinite) {
			return 0
		}
		return slidesToShow
	}

	const showPreviousArrow = (
		{ prevArrow, infinite }: SlideProps,
		currentIndex: number,
		moveSlides: ButtonClick,
	): ReactNode => {
		const isDisabled = currentIndex <= 0 && !infinite
		const props = {
			"data-type": "prev",
			"aria-label": "Previous Slide",
			disabled: isDisabled,
			onClick: moveSlides,
		}
		if (prevArrow) {
			return React.cloneElement(prevArrow, {
				className: cls(prevArrow.props.className, styles.nav, { [styles.disabled]: isDisabled }),
				...props,
			})
		}
		return (
			<button
				type="button"
				className={cls(styles.nav, styles.defaultNav, { [styles.disabled]: isDisabled })}
				{...props}>
				<svg width="24" height="24" viewBox="0 0 24 24">
					<path d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z" />
				</svg>
			</button>
		)
	}

	const showNextArrow = (
		properties: SlideProps,
		currentIndex: number,
		moveSlides: ButtonClick,
		responsiveSettings?: Responsive,
	) => {
		const { nextArrow, infinite, children } = properties
		let slidesToScroll = 1
		if (responsiveSettings) {
			slidesToScroll = responsiveSettings?.settings.slidesToScroll
		} else if ("slidesToScroll" in properties) {
			slidesToScroll = properties.slidesToScroll || 1
		}
		const isDisabled = currentIndex >= React.Children.count(children) - slidesToScroll && !infinite
		const props = {
			"data-type": "next",
			"aria-label": "Next Slide",
			disabled: isDisabled,
			onClick: moveSlides,
		}
		if (nextArrow) {
			return React.cloneElement(nextArrow, {
				className: cls(nextArrow.props.className, styles.nav, { [styles.disabled]: isDisabled }),
				...props,
			})
		}
		return (
			<button
				type="button"
				className={cls(styles.nav, styles.defaultNav, { [styles.disabled]: isDisabled })}
				{...props}>
				<svg width="24" height="24" viewBox="0 0 24 24">
					<path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
				</svg>
			</button>
		)
	}

	const style = {
		transform: `translateX(-${(index + getOffset()) * eachChildSize}px)`,
	}
	return (
		<div dir="ltr" aria-roledescription="carousel">
			<div
				className={styles.slideshowContainer}
				onMouseEnter={pauseSlides}
				onMouseOver={pauseSlides}
				onMouseLeave={startSlides}
				onMouseDown={startSwipe}
				onMouseUp={endSwipe}
				onMouseMove={swipe}
				onTouchStart={startSwipe}
				onTouchEnd={endSwipe}
				onTouchCancel={endSwipe}
				onTouchMove={swipe}>
				{props.arrows && showPreviousArrow(props, index, moveSlides)}
				<div className={cls(styles.slideshowWrapper, styles.slide, props.cssClass)} ref={wrapperRef}>
					<div className={cls(styles.imagesWrap, styles.horizontal)} style={style} ref={innerWrapperRef}>
						{props.infinite && renderPrecedingSlides()}
						{(React.Children.map(props.children, (thisArg) => thisArg) || []).map((each, key) => {
							const isThisSlideActive = isSlideActive(key)
							return (
								<div
									data-index={key}
									key={key}
									aria-roledescription="slide"
									aria-hidden={isThisSlideActive ? "false" : "true"}>
									{each}
								</div>
							)
						})}
						{renderTrailingSlides()}
					</div>
				</div>
				{props.arrows && showNextArrow(props, index, moveSlides, responsiveSettings)}
			</div>
		</div>
	)
})
